import React from 'react';
import { useSelector } from 'react-redux';
import { getSiteId } from 'services/app';
import { getPrimaryToken } from 'services/auth';
import { DropdownOption, GameType, IFortniteLeaderboardBlockData, LeaderboardType } from '../../utils';
import { getSeasons, getSessions, getTournaments } from './api';

const useFilters = (defaultFilters: IFortniteLeaderboardBlockData['defaultFilters'], fromBlockSettings?: boolean) => {
  const [leaderBoardType, setLeaderboardType] = React.useState(defaultFilters.leaderboardType);
  const [gameType, setGameType] = React.useState(defaultFilters.gameType);
  const [season, setSeason] = React.useState(defaultFilters.season);
  const [tournament, setTournament] = React.useState(defaultFilters.tournament);
  const [session, setSession] = React.useState(defaultFilters.session);

  const [tournaments, setTournaments] = React.useState<DropdownOption[]>([]);
  const [sessions, setSessions] = React.useState<DropdownOption[]>([]);
  const [seasons, setSeasons] = React.useState<DropdownOption[]>([]);

  const primaryToken = useSelector(getPrimaryToken) || '';
  const siteId = useSelector(getSiteId);

  const onChangeSeason = async (value: string) => {
    if (!value || season === value) {
      return;
    }

    setSeason(value);
    if (tournament) {
      setTournament('');
    }
    if (session) {
      setSession('');
    }
    if (tournaments.length > 0) {
      setTournaments([]);
    }
    if (sessions.length > 0) {
      setSessions([]);
    }

    const result = await getTournaments({
      season: value,
      gameType,
      primaryToken,
      siteId,
    });

    setTournaments(result);
  };

  const onChangeGameType = async (newGameType: GameType) => {
    if (gameType === newGameType) {
      return;
    }

    setGameType(newGameType);
    if (tournament) {
      setTournament('');
    }
    if (session) {
      setSession('');
    }
    if (seasons.length > 0) {
      setSeasons([]);
    }
    if (sessions.length > 0) {
      setSessions([]);
    }
    if (tournaments.length > 0) {
      setTournaments([]);
    }
    if (season) {
      setSeason('');
    }

    const result = await getSeasons({
      gameType: newGameType,
      primaryToken,
      siteId,
    });

    setSeasons(result);
  };

  const onChangeLeaderboardType = async (newLeaderboardType: LeaderboardType) => {
    if (leaderBoardType === newLeaderboardType) {
      return;
    }

    setLeaderboardType(newLeaderboardType);
    // TODO: Come back here when dealing with ranked leaderboards
  };

  const onChangeTournament = async (newTournament: string) => {
    if (!newTournament || tournament === newTournament) {
      return;
    }

    setTournament(newTournament);
    if (session) {
      setSession('');
    }
    if (sessions.length > 0) {
      setSessions([]);
    }

    const result = await getSessions({
      eventId: newTournament,
      primaryToken,
      siteId,
    });
    setSessions(result);
  };

  const onChangeSession = (newSession: string) => {
    if (!newSession || session === newSession) {
      return;
    }

    setSession(newSession);
  };

  React.useEffect(() => {
    let isMounted = true;

    getSeasons({
      gameType,
      primaryToken,
      siteId,
    }).then((result) => {
      if (isMounted) {
        setSeasons(result);
      }
    });

    if (season) {
      getTournaments({
        season,
        gameType,
        primaryToken,
        siteId,
      }).then((result) => {
        if (isMounted) {
          setTournaments(result);
        }
      });
    }

    if (tournament) {
      getSessions({
        eventId: tournament,
        primaryToken,
        siteId,
      }).then((result) => {
        if (isMounted) {
          setSessions(result);
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    seasons: {
      options: seasons,
      selected: seasons.find(s => s.value === season),
      onChange: onChangeSeason,
    },
    tournaments: {
      options: tournaments,
      selected: tournaments.find(t => t.value === tournament),
      onChange: onChangeTournament,
    },
    sessions: {
      options: sessions,
      selected: sessions.find(s => s.value === session),
      onChange: onChangeSession,
    },
    isRanked: leaderBoardType === 'ranked',
    isBattleRoyale: gameType === 'battle-royale',
    onChangeGameType,
    onChangeLeaderboardType,
    tournament,
    session,
  };
};

export default useFilters;
